"use client";

import { ReactNode } from "react";
import { useSearchParams } from "next/navigation";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/shadcn/scroll-area";
import { Tabs, TabsContent } from "@/components/shadcn/tabs";
import { NoxusLoadingPulse } from "@/components/UI/NoxusLoadingPulse";
import { IBreadcrumb, PageHeader } from "@/components/Layouts/PageHeader";

export interface ISection {
  id: string;
  title: string;
  icon?: ReactNode;
  content: ReactNode;
}

interface StandardLayoutProps {
  // With sections, a string title also becomes the crumb before the
  // section's title.
  title?: string | ReactNode;
  breadcrumbs?: IBreadcrumb[];
  headerActions?: ReactNode;
  children?: ReactNode;
  // A page whose sections are listed in the sidenav: the URL's `?tab=` picks
  // the one shown, and the header is titled by it. With none, `children` is
  // the body (also a sectioned page's error state).
  sections?: ISection[];
  // Overrides the section `?tab=` names (a page that resolves legacy or gated
  // values itself).
  activeSection?: string;
  className?: string;
  noPadding?: boolean;
  // For pages that manage their own internal scrolling (split panes, pinned
  // action bars). Skips the ScrollArea: its display:table viewport wrapper
  // breaks the height chain, so h-full descendants grow with content instead
  // of filling the viewport.
  noScroll?: boolean;
  // When true, keeps the header shell mounted and renders a loader in the
  // content area instead of children — so the page doesn't flash its shell
  // away while initial data is in flight.
  isLoading?: boolean;
}

export const StandardLayout = ({
  title,
  breadcrumbs,
  headerActions,
  children,
  sections = [],
  activeSection,
  className,
  noPadding = false,
  noScroll = false,
  isLoading = false,
}: StandardLayoutProps) => {
  const searchParams = useSearchParams();
  const picked = activeSection ?? searchParams?.get("tab");
  const selected =
    sections.find((section) => section.id === picked) ?? sections[0];
  const pageCrumb =
    selected && typeof title === "string" ? [{ label: title }] : [];

  const paddingClass = noPadding ? "p-0" : "px-4 pt-4 pb-4";
  const body = isLoading ? (
    <NoxusLoadingPulse variant="blocks" />
  ) : selected ? (
    // `contents`: the panels lay out as the body's own children.
    <Tabs value={selected.id} className="contents">
      {sections.map((section) => (
        <TabsContent
          key={section.id}
          value={section.id}
          aria-label={section.title}
          className="mt-0 h-full flex flex-col focus-visible:ring-0"
        >
          {section.content}
        </TabsContent>
      ))}
    </Tabs>
  ) : (
    children
  );

  return (
    <div
      className={cn("flex flex-col h-full w-full overflow-hidden", className)}
    >
      <PageHeader
        title={selected?.title ?? title}
        breadcrumbs={[...(breadcrumbs ?? []), ...pageCrumb]}
        actions={!isLoading && headerActions}
      />
      <main className="flex-1 min-h-0 overflow-hidden">
        {noScroll ? (
          <div className={cn("h-full", paddingClass)}>{body}</div>
        ) : (
          <ScrollArea className="h-full" constrainWidth>
            <div className={cn("min-h-full", paddingClass)}>{body}</div>
          </ScrollArea>
        )}
      </main>
    </div>
  );
};
